import { FC, useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  CircularProgress,
  Autocomplete,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import ReactApexChart from "react-apexcharts";
import { useGetAverageUnitCostQuery, AverageUnitCostManufacturerOption, AverageUnitCostPlotPoint } from "@/services/dashboard/dashboard-api";
import { formatNumberWithSuffix, numberWithCommas, formatHTSCode } from "@/utils";
import { ApexOptions } from "apexcharts";
import { format } from "date-fns";
import { DateRangeFilter } from "@/components";
import { Range } from "react-date-range";

interface UnitCostAnalysisSectionProps {
  htsCode: string;
  defaultOrigin?: string;
  clientId?: string;
}

const UnitCostAnalysisSection: FC<UnitCostAnalysisSectionProps> = ({
  htsCode,
  defaultOrigin,
  clientId
}) => {
  const [selectedOrigins, setSelectedOrigins] = useState<string[]>([]);
  // Fix state type to match API option type
  const [selectedManufacturers, setSelectedManufacturers] = useState<AverageUnitCostManufacturerOption[]>([]);
  // Selected entries from scatter plot clicks
  const [selectedEntries, setSelectedEntries] = useState<AverageUnitCostPlotPoint[]>([]);

  // Date Range State using same type as DateRangeFilter
  const [dateFilter, setDateFilter] = useState<Pick<Range, "startDate" | "endDate">>({
    startDate: new Date(new Date().setFullYear(new Date().getFullYear() - 1)), // Default to last 1 year
    endDate: new Date()
  });

  // Initialize selected origins with default if provided and not already set (on first load)
  useEffect(() => {
    if (defaultOrigin && selectedOrigins.length === 0) {
      setSelectedOrigins([defaultOrigin]);
    }
  }, [defaultOrigin]);

  // Clear selected entries when filters change
  useEffect(() => {
    setSelectedEntries([]);
  }, [htsCode, selectedOrigins, selectedManufacturers, dateFilter]);

  const { data: response, isFetching } = useGetAverageUnitCostQuery({
    input: {
      htsCode: htsCode.replace(/\./g, ''), // Ensure clean HTS
      clientId,
      originCodes: selectedOrigins,
      // Fix: map using manufacturerID which is guaranteed to be present in the option objects
      manufacturerIds: selectedManufacturers.map(m => m.manufacturerID),
      dateFrom: dateFilter.startDate?.toISOString(),
      dateTo: dateFilter.endDate?.toISOString(),
      includeOptions: true
    }
  }, {
    skip: !htsCode
  });

  const data = response?.data;

  // Use ref to store current plotData to avoid stale closures in chart events
  const plotDataRef = useRef<AverageUnitCostPlotPoint[]>([]);
  const selectedEntriesRef = useRef<AverageUnitCostPlotPoint[]>([]);

  // Keep refs in sync with state
  useEffect(() => {
    plotDataRef.current = data?.plotData || [];
  }, [data?.plotData]);

  useEffect(() => {
    selectedEntriesRef.current = selectedEntries;
  }, [selectedEntries]);

  // Handle scatter plot point click - uses refs to avoid stale closures
  const handleMarkerClick = useCallback((seriesIndex: number, dataPointIndex: number) => {
    // Only handle clicks on scatter series (index 1)
    if (seriesIndex !== 1) return;

    const currentPlotData = plotDataRef.current;
    const clickedEntry = currentPlotData[dataPointIndex];
    if (!clickedEntry) return;

    // Check if entry is already selected (by entryLine or date+unitCost combo)
    const currentSelected = selectedEntriesRef.current;
    const isAlreadySelected = currentSelected.some(
      e => e.entryLine === clickedEntry.entryLine ||
           (e.date === clickedEntry.date && e.unitCost === clickedEntry.unitCost)
    );

    if (!isAlreadySelected) {
      setSelectedEntries(prev => [...prev, clickedEntry]);
    }
  }, []);

  // Remove entry from selected list
  const handleRemoveEntry = useCallback((entry: AverageUnitCostPlotPoint) => {
    setSelectedEntries(prev => prev.filter(
      e => !(e.entryLine === entry.entryLine ||
             (e.date === entry.date && e.unitCost === entry.unitCost))
    ));
  }, []);

  // Chart Configuration
  const chartOptions: ApexOptions = useMemo(() => ({
    chart: {
      height: 350,
      type: 'line',
      toolbar: { show: false },
      zoom: { enabled: true },
      events: {
        markerClick: (_event: unknown, _chartContext: unknown, opts: { seriesIndex: number; dataPointIndex: number }) => {
          handleMarkerClick(opts.seriesIndex, opts.dataPointIndex);
        }
      }
    },
    stroke: {
      width: [3, 0], // Line for average, 0 for scatter
      curve: 'smooth'
    },
    fill: {
      type: ['solid', 'solid'],
      opacity: [1, 0.5]
    },
    markers: {
      size: [0, 8], // No markers for line, 8px for scatter (larger for easier clicking)
      colors: ['#0B494B', '#1CB4BA'],
      strokeColors: '#fff',
      strokeWidth: 2,
      hover: {
        size: 10,
        sizeOffset: 3
      },
      showNullDataPoints: false
    },
    states: {
      hover: {
        filter: {
          type: 'lighten',
          value: 0.15
        }
      },
      active: {
        allowMultipleDataPointsSelection: true,
        filter: {
          type: 'darken',
          value: 0.35
        }
      }
    },
    colors: ['#0B494B', '#1CB4BA'], // Brand colors
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeFormatter: {
          year: 'yyyy',
          month: 'MMM \'yy',
          day: 'dd MMM',
          hour: 'HH:mm'
        },
        style: { fontFamily: 'Inter' }
      },
      tooltip: { enabled: false }
    },
    yaxis: {
      title: { 
        text: 'Unit Cost ($)',
        style: { fontFamily: 'Inter', fontWeight: 500 }
      },
      labels: {
        formatter: (val) => formatNumberWithSuffix(val),
        style: { fontFamily: 'Inter' }
      }
    },
    tooltip: {
      shared: false, // Fix: Use unshared tooltip for better scatter plot interaction
      intersect: true, // Fix: Only show tooltip when hovering directly over point
      custom: function({ seriesIndex, dataPointIndex, w }) {
        const point = w.config.series[seriesIndex].data[dataPointIndex];
        const val = point.y;
        
        // Differentiate tooltip content based on series type
        if (seriesIndex === 0) { // Line chart (Average)
            return (
                '<div class="arrow_box" style="padding: 8px; background: white; border: 1px solid #ccc; border-radius: 4px;">' +
                '<span>' + format(new Date(point.x), 'MMM yyyy') + '</span><br>' +
                '<b>Avg Cost: $' + formatNumberWithSuffix(val) + '</b>' +
                '</div>'
            );
        } else { // Scatter plot (Individual)
            // We can try to attach extra data to the point if available in series construction
            // For now, standard display
            return (
                '<div class="arrow_box" style="padding: 8px; background: white; border: 1px solid #ccc; border-radius: 4px;">' +
                '<span>' + format(new Date(point.x), 'dd MMM yyyy') + '</span><br>' +
                '<b>Unit Cost: $' + formatNumberWithSuffix(val) + '</b>' +
                '</div>'
            );
        }
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      fontFamily: 'Inter'
    },
    grid: {
      borderColor: '#f1f1f1'
    }
  }), [handleMarkerClick]);

  const series = useMemo(() => {
    if (!data) return [];

    const lineData = (data.chartData || []).map(d => ({
      x: new Date(d.year, d.month - 1, 1).getTime(),
      y: d.averageUnitCost
    }));

    const scatterData = (data.plotData || []).map(d => ({
      x: new Date(d.date).getTime(),
      y: d.unitCost
    }));

    return [
      {
        name: 'Avg Unit Cost (Monthly)',
        type: 'line',
        data: lineData
      },
      {
        name: 'Individual Shipments',
        type: 'scatter',
        data: scatterData
      }
    ];
  }, [data]);

  if (!htsCode) return null;

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', mt: 3 }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
            <Typography variant="h6" fontWeight={700} color="#0B494B">
                Unit Cost Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Analyze historical unit costs for this HTS code.
            </Typography>
            </Box>
            
            <Box>
                <DateRangeFilter 
                    value={dateFilter} 
                    onSelectDate={setDateFilter} 
                />
            </Box>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Autocomplete
            multiple
            options={data?.availableOriginCodes || []}
            value={selectedOrigins}
            onChange={(_, newValue) => setSelectedOrigins(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Origins" placeholder="Select origins" />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
              ))
            }
            sx={{ minWidth: 200, flex: 1 }}
            loading={isFetching}
          />

          <Autocomplete
            multiple
            options={data?.availableManufacturers || []}
            getOptionLabel={(option) => option.manufacturerName || option.manufacturerID}
            value={selectedManufacturers}
            onChange={(_, newValue) => setSelectedManufacturers(newValue)}
            isOptionEqualToValue={(option, value) => option.manufacturerID === value.manufacturerID}
            renderInput={(params) => (
              <TextField {...params} label="Manufacturers" placeholder="Select manufacturers" />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box>
                  <Typography variant="body2">{option.manufacturerName || option.manufacturerID}</Typography>
                  {option.value !== undefined && (
                    <Typography variant="caption" color="text.secondary">
                      ${formatNumberWithSuffix(option.value)}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
             renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option.manufacturerName || option.manufacturerID} size="small" {...getTagProps({ index })} />
              ))
            }
            sx={{ minWidth: 200, flex: 1 }}
            loading={isFetching}
          />
        </Stack>

        <Box sx={{ minHeight: 350, position: 'relative' }}>
          {isFetching && (
            <Stack 
              alignItems="center" 
              justifyContent="center" 
              sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255,255,255,0.7)', zIndex: 1 }}
            >
              <CircularProgress />
            </Stack>
          )}
          
          {data && (series[0].data.length > 0 || series[1].data.length > 0) ? (
            <ReactApexChart 
              options={chartOptions} 
              series={series} 
              type="line" 
              height={350} 
            />
          ) : !isFetching ? (
            <Stack alignItems="center" justifyContent="center" height="100%">
              <Typography color="text.secondary">No unit cost data available for the selected filters.</Typography>
            </Stack>
          ) : null}
        </Box>
        
        {data && (
           <Stack direction="row" spacing={4} justifyContent="center" sx={{ pt: 2, borderTop: '1px solid #f0f0f0' }}>
              <Box textAlign="center">
                 <Typography variant="caption" color="text.secondary">Average Unit Cost</Typography>
                 <Typography variant="h6" fontWeight={700}>${formatNumberWithSuffix(data.averageUnitCost)}</Typography>
              </Box>
              <Box textAlign="center">
                 <Typography variant="caption" color="text.secondary">Total Quantity</Typography>
                 <Typography variant="h6" fontWeight={700}>{formatNumberWithSuffix(data.totalQuantity)}</Typography>
              </Box>
              <Box textAlign="center">
                 <Typography variant="caption" color="text.secondary">Total Value</Typography>
                 <Typography variant="h6" fontWeight={700}>${formatNumberWithSuffix(data.totalGoodsValue)}</Typography>
              </Box>
           </Stack>
        )}

        {/* Selected Entry Lines Table */}
        {selectedEntries.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} color="#0B494B">
                  Selected Entry Lines ({selectedEntries.length})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Click on scatter plot points to add entry lines
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => setSelectedEntries([])}
                sx={{ color: 'text.secondary' }}
              >
                Clear All
              </Button>
            </Stack>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: '#f5f5f5' }}>Date</TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5' }}>Entry Summary + Line</TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5' }}>HTS Code</TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5' }}>Origin</TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5' }}>Manufacturer</TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5' }} align="right">Unit Cost</TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5' }} align="right">Qty</TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5' }} align="right">Value</TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5' }} align="right">Line Duties</TableCell>
                    <TableCell sx={{ bgcolor: '#f5f5f5' }} align="center"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedEntries.map((entry, index) => {
                    const lineDuties = (entry.mfnDuty || 0) + (entry.duty301 || 0) + (entry.duty232 || 0) + (entry.oth99Duties || 0);
                    return (
                      <TableRow key={entry.entryLine || `${entry.date}-${index}`} hover>
                        <TableCell>{format(new Date(entry.date), 'dd MMM yyyy')}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {entry.entryLine || '-'}
                        </TableCell>
                        <TableCell>{entry.htsNumberFull ? formatHTSCode(entry.htsNumberFull) : '-'}</TableCell>
                        <TableCell>{entry.countryOfOriginCode || '-'}</TableCell>
                        <TableCell>{entry.manufacturerID || '-'}</TableCell>
                        <TableCell align="right">${formatNumberWithSuffix(entry.unitCost)}</TableCell>
                        <TableCell align="right">{numberWithCommas(entry.quantity)}</TableCell>
                        <TableCell align="right">${formatNumberWithSuffix(entry.value)}</TableCell>
                        <TableCell align="right">
                          {lineDuties > 0 ? `$${formatNumberWithSuffix(lineDuties)}` : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveEntry(entry)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default UnitCostAnalysisSection;
